import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Autocomplete, Avatar, Box, Button, Card, Chip, CircularProgress, Divider, FormControlLabel,
  Grid, IconButton, InputAdornment, MenuItem, Select, Stack, Switch, Tab, Tabs, TextField, Tooltip,
  Typography, useMediaQuery, useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import { CiSearch } from "react-icons/ci";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import {
  useLazySearchSessionStudentsQuery, useLazySearchAbaStaffQuery,
  useValidateProgramAssignmentsMutation, useSaveProgramAssignmentsMutation,
  useListProgramAssignmentsQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { AppDatePicker } from "../../../Components/DatePicker";

/**
 * Batch 38 — Students & Staff caseload workspace.
 *
 * Left panel: async ABA Student search + Add.
 * Right panel: selected Students, each with Staff picker (Async, ABA-filtered),
 * dates, overrides, session frequency, notes.
 *
 * Sticky footer runs the validation endpoint on demand and Save-and-Activate.
 */
type SelectedStudent = {
  id: number; label: string;
  grade_id?: number|null; location_id?: number|null;
  active_programs_count?: number;
  staff?: { id: number; label: string; availability_status?: string; location_mismatch?: boolean } | null;
  startDate?: string;
  reviewDate?: string;
  targetSubset?: number[] | null; // null = all active
  promptTemplateIdOverride?: number | null;
  masteryWorkflowIdOverride?: number | null;
  sessionFrequency?: string;
  notes?: string;
  status?: string;
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active", paused: "Paused", ended: "Ended", pending_review: "Pending Review",
};
const AVAILABILITY_META: Record<string, { label: string; color: any }> = {
  available:         { label: "Available", color: "success" },
  near_capacity:     { label: "Near Capacity", color: "warning" },
  at_capacity:       { label: "At Capacity", color: "error" },
  location_mismatch: { label: "Location Mismatch", color: "warning" },
};

export default function StudentsStaffWorkspace({
  programId, promptTemplates, workflows, initialTargets,
}: {
  programId: number | null;
  promptTemplates: Array<{ id: number|string; templateName: string }>;
  workflows: Array<{ id: number|string; workflowName: string }>;
  initialTargets: Array<{ id: number; targetName: string; status?: string; measurementType?: string }>;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showMessage, showErrorMessage } = useNotifier();

  const [mobileTab, setMobileTab] = useState(0);
  const [selected, setSelected] = useState<SelectedStudent[]>([]);
  const [validation, setValidation] = useState<Record<number, { errors: string[]; warnings: string[]; blocking: boolean }>>({});

  // Left-panel search state
  const [q, setQ] = useState("");
  const [locationId, setLocationId] = useState<string>("");
  const [hasProgramFilter, setHasProgramFilter] = useState<"any" | "with" | "without">("any");
  const debounced = useDebounce(q, 400);

  const [searchStudents] = useLazySearchSessionStudentsQuery();
  const [searchStaff] = useLazySearchAbaStaffQuery();
  const [validate] = useValidateProgramAssignmentsMutation();
  const [save] = useSaveProgramAssignmentsMutation();
  const { data: assignmentsData, refetch: refetchAssignments } = useListProgramAssignmentsQuery(
    programId ?? 0, { skip: !programId }
  );

  // Async student results
  const [studentResults, setStudentResults] = useState<any[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const searchIdRef = useRef(0);
  // Fetches the eligible ABA student list immediately on mount and
  // whenever the search text or filters change. Empty `q` is a valid
  // request — the backend returns the first `limit` rows scoped to
  // Special-Program (`aba` groupType). Previously guarded to skip
  // requests until the user had typed 3+ chars, which meant the panel
  // rendered as an empty search box instead of a student browser.
  useEffect(() => {
    setLoadingStudents(true);
    const rid = ++searchIdRef.current;
    searchStudents({
      q: debounced || "",
      groupType: "aba",
      locationId: locationId || undefined,
      // 20 is the backend cap; gives admins a useful default page
      // without any front-end pagination scaffolding.
      limit: 20,
    } as any)
      .then((r: any) => {
        if (rid !== searchIdRef.current) return;
        const data: any[] = r?.data?.data || [];
        const filtered = hasProgramFilter === "with"
          ? data.filter((d) => (d.active_programs_count ?? 0) > 0)
          : hasProgramFilter === "without"
            ? data.filter((d) => (d.active_programs_count ?? 0) === 0)
            : data;
        setStudentResults(filtered);
      })
      .finally(() => { if (rid === searchIdRef.current) setLoadingStudents(false); });
  }, [debounced, locationId, hasProgramFilter]);

  // Rehydrate from server on load.
  useEffect(() => {
    const rows: any[] = Array.isArray(assignmentsData?.data) ? (assignmentsData!.data as any[]) : [];
    if (rows.length === 0) return;
    setSelected(rows.map((r) => ({
      id: r.studentId, label: r.studentName || `Student #${r.studentId}`,
      staff: r.staffId ? { id: r.staffId, label: r.staffName || `Staff #${r.staffId}` } : null,
      startDate: r.startDate || "", reviewDate: r.reviewDate || "",
      targetSubset: (() => { try { return r.targetSubset ? JSON.parse(r.targetSubset) : null; } catch { return null; } })(),
      promptTemplateIdOverride: r.promptTemplateIdOverride ?? null,
      masteryWorkflowIdOverride: r.masteryWorkflowIdOverride ?? null,
      sessionFrequency: r.sessionFrequency || "as_scheduled",
      notes: r.notes || "",
      status: r.status || "active",
    })));
  }, [assignmentsData]);

  const alreadySelected = (id: number) => selected.some((s) => s.id === id);
  const addStudent = (s: any) => {
    if (alreadySelected(s.id)) return;
    setSelected((L) => [...L, {
      id: s.id, label: s.label,
      grade_id: s.grade_id, location_id: s.location_id,
      active_programs_count: s.active_programs_count,
      staff: null, startDate: new Date().toISOString().slice(0, 10),
      reviewDate: "", targetSubset: null,
      promptTemplateIdOverride: null, masteryWorkflowIdOverride: null,
      sessionFrequency: "as_scheduled", notes: "", status: "active",
    }]);
  };
  const removeStudent = (id: number) => setSelected((L) => L.filter((s) => s.id !== id));
  const patch = (id: number, p: Partial<SelectedStudent>) =>
    setSelected((L) => L.map((s) => s.id === id ? { ...s, ...p } : s));

  const runValidate = async () => {
    if (!programId) return;
    const payload = selected.map((s) => ({
      studentId: s.id, staffId: s.staff?.id ?? null,
      startDate: s.startDate || null, reviewDate: s.reviewDate || null,
      targetSubset: s.targetSubset ?? null,
      promptTemplateIdOverride: s.promptTemplateIdOverride ?? null,
      masteryWorkflowIdOverride: s.masteryWorkflowIdOverride ?? null,
      sessionFrequency: s.sessionFrequency ?? null,
      notes: s.notes ?? null,
      status: s.status ?? "active",
    }));
    const r: any = await validate({ program_id: programId, assignments: payload }).unwrap();
    if (r?.status) {
      const map: any = {};
      (r.data || []).forEach((v: any) => { if (v.studentId) map[v.studentId] = v; });
      setValidation(map);
    }
  };
  useEffect(() => { if (programId && selected.length) runValidate(); }, [selected.length, programId]);

  const doSave = async () => {
    if (!programId) { showErrorMessage("Save the Program first, then add assignments."); return; }
    if (selected.length === 0) { showErrorMessage("Add at least one Student."); return; }
    const payload = selected.map((s) => ({
      studentId: s.id, staffId: s.staff?.id ?? null,
      startDate: s.startDate || null, reviewDate: s.reviewDate || null,
      targetSubset: s.targetSubset ?? null,
      promptTemplateIdOverride: s.promptTemplateIdOverride ?? null,
      masteryWorkflowIdOverride: s.masteryWorkflowIdOverride ?? null,
      sessionFrequency: s.sessionFrequency ?? null,
      notes: s.notes ?? null,
      status: s.status ?? "active",
    }));
    try {
      const r: any = await save({ program_id: programId, assignments: payload }).unwrap();
      if (r?.status) { showMessage("Assignments saved."); refetchAssignments(); }
      else showErrorMessage(r?.message || "Save failed.");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Save failed."); }
  };

  // Summary counts
  const summary = useMemo(() => {
    const selCount = selected.length;
    const staffCount = new Set(selected.map((s) => s.staff?.id).filter(Boolean)).size;
    const targetCount = selected.reduce((n, s) => n + (s.targetSubset?.length ?? initialTargets.length), 0);
    const missingStaff = selected.filter((s) => !s.staff).length;
    const blocking = Object.values(validation).filter((v) => v.blocking).length;
    const locationWarn = Object.values(validation).filter((v) => v.warnings?.some((w) => w.includes("location"))).length;
    return { selCount, staffCount, targetCount, missingStaff, blocking, locationWarn };
  }, [selected, validation, initialTargets.length]);

  // Panels ————————————————————————————————————————————
  const AvailablePanel = (
    <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Typography variant="subtitle1" fontWeight={700}>Available ABA Students</Typography>
      <Typography variant="caption" color="text.secondary">Active Special Program students. New ABA students without a Program still appear.</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1, mb: 1 }}>
        <TextField size="small" fullWidth placeholder="Search student…" value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch /></InputAdornment> }} />
        <TextField size="small" placeholder="Location ID" value={locationId}
          onChange={(e) => setLocationId(e.target.value)} sx={{ width: 140 }} />
        <Select size="small" value={hasProgramFilter} onChange={(e) => setHasProgramFilter(e.target.value as any)} sx={{ minWidth: 180 }}>
          <MenuItem value="any">Any assignment</MenuItem>
          <MenuItem value="with">Has active ABA Program</MenuItem>
          <MenuItem value="without">Has no active ABA Program</MenuItem>
        </Select>
      </Stack>
      <Divider sx={{ mb: 1 }} />
      {loadingStudents && studentResults.length === 0 ? (
        <Box sx={{ p: 2, textAlign: "center" }}><CircularProgress size={20} /></Box>
      ) : studentResults.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {q.trim() || locationId || hasProgramFilter !== "any"
            ? "No students match your search."
            : "No eligible ABA students found."}
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ maxHeight: 480, overflowY: "auto" }}>
          {studentResults.map((s) => {
            const already = alreadySelected(s.id);
            return (
              <Card key={s.id} variant="outlined" sx={{ p: 1 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Avatar sx={{ width: 32, height: 32 }}><PersonIcon fontSize="small" /></Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{s.label}</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {s.aba && <Chip size="small" color="primary" variant="outlined" label="ABA" />}
                      {s.grade_id && <Chip size="small" variant="outlined" label={`Grade ${s.grade_id}`} />}
                      {s.location_id && <Chip size="small" variant="outlined" label={`Loc ${s.location_id}`} />}
                      {typeof s.active_programs_count === "number" && (
                        <Chip size="small" variant="outlined" label={`${s.active_programs_count} Program${s.active_programs_count === 1 ? "" : "s"}`} />
                      )}
                      {s.last_aba_session_at && <Chip size="small" variant="outlined" label={`Last ${new Date(s.last_aba_session_at).toLocaleDateString()}`} />}
                    </Stack>
                  </Box>
                  <Tooltip title={already ? "Already assigned to this Program" : "Add Student"}>
                    <span>
                      <Button size="small" variant={already ? "outlined" : "contained"} startIcon={<AddIcon />}
                        disabled={already} onClick={() => addStudent(s)}>
                        {already ? "Added" : "Add"}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}
    </Card>
  );

  const SelectedPanel = (
    <Card variant="outlined" sx={{ p: 2, height: "100%" }} data-testid="selected-panel">
      <Typography variant="subtitle1" fontWeight={700}>Selected Students &amp; Staff</Typography>
      {selected.length === 0 ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          Add ABA Students from the left panel to configure Staff and Targets.
        </Alert>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {selected.map((s) => (
            <SelectedStudentCard key={s.id}
              value={s}
              validation={validation[s.id]}
              targets={initialTargets}
              promptTemplates={promptTemplates}
              workflows={workflows}
              onChange={(p) => patch(s.id, p)}
              onRemove={() => removeStudent(s.id)}
              onSearchStaff={(qq) => searchStaff({ q: qq, student_id: s.id } as any).then((r: any) => r?.data?.data || [])}
            />
          ))}
        </Stack>
      )}
    </Card>
  );

  // Sticky footer summary + save
  const Footer = (
    <Card variant="outlined" sx={{ p: 2, mt: 2, position: "sticky", bottom: 0, bgcolor: "background.paper" }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`${summary.selCount} Students selected`} />
            <Chip size="small" label={`${summary.staffCount} Staff assigned`} />
            <Chip size="small" label={`${summary.targetCount} Targets included`} />
            {summary.missingStaff > 0 && <Chip size="small" color="warning" label={`${summary.missingStaff} needs Staff`} />}
            {summary.locationWarn > 0 && <Chip size="small" color="warning" label={`${summary.locationWarn} location warning${summary.locationWarn === 1 ? "" : "s"}`} />}
            {summary.blocking > 0 && <Chip size="small" color="error" label={`${summary.blocking} blocking issue${summary.blocking === 1 ? "" : "s"}`} />}
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pr: { xs: 0, md: "80px" } }}>
            <Button variant="outlined" onClick={runValidate} disabled={!programId || selected.length === 0}>Validate</Button>
            <Button variant="contained" onClick={doSave} disabled={!programId || selected.length === 0 || summary.blocking > 0}>
              Save Assignments
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );

  if (isMobile) {
    return (
      <Box>
        <Tabs value={mobileTab} onChange={(_, v) => setMobileTab(v)} variant="fullWidth" sx={{ mb: 1 }}>
          <Tab label="Available Students" />
          <Tab label={`Selected (${selected.length})`} />
        </Tabs>
        {mobileTab === 0 ? AvailablePanel : SelectedPanel}
        {Footer}
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>{AvailablePanel}</Grid>
        <Grid item xs={12} md={7}>{SelectedPanel}</Grid>
      </Grid>
      {Footer}
    </Box>
  );
}

// ------- selected student card (staff picker + fields + validation banner)

function SelectedStudentCard(props: {
  value: SelectedStudent;
  validation?: { errors: string[]; warnings: string[]; blocking: boolean };
  targets: Array<{ id: number; targetName: string; status?: string; measurementType?: string }>;
  promptTemplates: Array<{ id: number|string; templateName: string }>;
  workflows: Array<{ id: number|string; workflowName: string }>;
  onChange: (p: Partial<SelectedStudent>) => void;
  onRemove: () => void;
  onSearchStaff: (q: string) => Promise<any[]>;
}) {
  const { value: v, validation, targets, promptTemplates, workflows, onChange, onRemove, onSearchStaff } = props;
  const [staffOpts, setStaffOpts] = useState<any[]>(v.staff ? [v.staff] : []);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffQ, setStaffQ] = useState("");
  const staffDeb = useDebounce(staffQ, 400);
  useEffect(() => {
    setStaffLoading(true);
    onSearchStaff(staffDeb || "")
      .then((data) => setStaffOpts(data || []))
      .finally(() => setStaffLoading(false));
  }, [staffDeb]);

  const [subsetMode, setSubsetMode] = useState<"all" | "custom">(v.targetSubset && v.targetSubset.length ? "custom" : "all");

  return (
    <Card variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Avatar sx={{ width: 28, height: 28 }}><PersonIcon fontSize="small" /></Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap>{v.label}</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Chip size="small" color="primary" variant="outlined" label="ABA" />
            {v.grade_id && <Chip size="small" variant="outlined" label={`Grade ${v.grade_id}`} />}
            {v.location_id && <Chip size="small" variant="outlined" label={`Loc ${v.location_id}`} />}
            <Chip size="small" label={STATUS_LABELS[v.status || "active"] || v.status} />
          </Stack>
        </Box>
        <IconButton size="small" onClick={onRemove} aria-label="remove-student"><RemoveIcon fontSize="small" /></IconButton>
      </Stack>

      {validation && (validation.errors.length > 0 || validation.warnings.length > 0) && (
        <Box sx={{ mt: 1 }}>
          {validation.errors.map((e, i) => <Alert key={"e" + i} severity="error" sx={{ mb: 0.5 }}>{e}</Alert>)}
          {validation.warnings.map((w, i) => <Alert key={"w" + i} severity="warning" sx={{ mb: 0.5 }}>{w}</Alert>)}
        </Box>
      )}

      <Grid container spacing={1} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Autocomplete size="small"
            value={v.staff}
            onChange={(_, val: any) => onChange({ staff: val })}
            onInputChange={(_, val) => setStaffQ(val)}
            options={staffOpts}
            loading={staffLoading}
            getOptionLabel={(o: any) => o?.label ?? ""}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderOption={(op, o: any) => (
              <Box component="li" {...op}>
                <Stack sx={{ flexGrow: 1 }}>
                  <Typography variant="body2">{o.label} {o.role && <Typography component="span" variant="caption" color="text.secondary"> · {o.role}</Typography>}</Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {AVAILABILITY_META[o.availability_status] && (
                      <Chip size="small" color={AVAILABILITY_META[o.availability_status].color as any} label={AVAILABILITY_META[o.availability_status].label} />
                    )}
                    {o.assigned_to_student && <Chip size="small" color="info" label="Already assigned" />}
                    <Chip size="small" variant="outlined" label={`${o.caseload_count}/${o.capacity} caseload`} />
                  </Stack>
                </Stack>
              </Box>
            )}
            renderInput={(p) => <TextField {...p} label="Assigned ABA Staff *" placeholder="Type 3+ characters" />}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <AppDatePicker label="Start Date" required size="small" fullWidth
            value={v.startDate || ""} onChange={(iso) => onChange({ startDate: iso })} />
        </Grid>
        <Grid item xs={6} md={3}>
          <AppDatePicker label="Review Date" size="small" fullWidth
            value={v.reviewDate || ""} onChange={(iso) => onChange({ reviewDate: iso })} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Select size="small" fullWidth value={subsetMode}
            onChange={(e) => { const m = e.target.value as any; setSubsetMode(m); if (m === "all") onChange({ targetSubset: null }); }}>
            <MenuItem value="all">All active Targets</MenuItem>
            <MenuItem value="custom">Select specific Targets</MenuItem>
          </Select>
        </Grid>
        {subsetMode === "custom" && (
          <Grid item xs={12} md={8}>
            <Autocomplete size="small" multiple options={targets}
              getOptionLabel={(o) => o.targetName}
              value={targets.filter((t) => (v.targetSubset ?? []).includes(t.id))}
              onChange={(_, vals) => onChange({ targetSubset: vals.map((t) => t.id) })}
              renderInput={(p) => <TextField {...p} label="Target Subset" placeholder="Choose Targets" />} />
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <Select size="small" fullWidth displayEmpty value={v.promptTemplateIdOverride ?? ""}
            onChange={(e) => onChange({ promptTemplateIdOverride: e.target.value ? Number(e.target.value) : null })}>
            <MenuItem value=""><em>Use Program Default (Prompt Levels)</em></MenuItem>
            {promptTemplates.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.templateName}</MenuItem>)}
          </Select>
        </Grid>
        <Grid item xs={12} md={4}>
          <Select size="small" fullWidth displayEmpty value={v.masteryWorkflowIdOverride ?? ""}
            onChange={(e) => onChange({ masteryWorkflowIdOverride: e.target.value ? Number(e.target.value) : null })}>
            <MenuItem value=""><em>Use Program Default (Workflow)</em></MenuItem>
            {workflows.map((w) => <MenuItem key={w.id} value={String(w.id)}>{w.workflowName}</MenuItem>)}
          </Select>
        </Grid>
        <Grid item xs={12} md={4}>
          <Select size="small" fullWidth value={v.sessionFrequency || "as_scheduled"}
            onChange={(e) => onChange({ sessionFrequency: e.target.value })}>
            <MenuItem value="as_scheduled">As Scheduled</MenuItem>
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="multi_weekly">Multiple times per week</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={12}>
          <TextField size="small" fullWidth multiline minRows={1} label="Assignment Notes"
            value={v.notes || ""} onChange={(e) => onChange({ notes: e.target.value })} />
        </Grid>
      </Grid>
    </Card>
  );
}
